import { ListChecks } from '@phosphor-icons/react';
import { Button } from '../../../components/ui/button';
import type { ExpectedField, ImportBatchRow } from '@discovr/types';
import { computeImportTally, confirmRowSummaries, type ConfirmRowSummary } from './importWizardHelpers';
import { WizardFooter } from './ImportWizardFooterSlot';
import { RowNumberBadge } from './ImportRowBadge';
import { cn } from '../../../lib/utils';
import { TruncatedText } from '../../../components/common/TruncatedText';

interface ImportConfirmStepProps {
  rows: ImportBatchRow[];
  expectedFields: ExpectedField[];
  busy: boolean;
  onCommit: () => Promise<void>;
  onBack: () => Promise<void>;
}

const VERB_LABEL: Record<ConfirmRowSummary['verb'], string> = {
  add: 'New',
  update: 'Update',
  keep: 'No change',
  skip: 'Skip',
};

const VERB_TONE: Record<ConfirmRowSummary['verb'], string> = {
  add: 'bg-emerald-50 text-emerald-700',
  update: 'bg-blue-50 text-blue-700',
  keep: 'bg-muted text-muted-foreground',
  skip: 'bg-muted text-muted-foreground',
};

/** The pill alone fully explains add/keep/skip — a detail line under it
 *  would just restate the pill in different words ("New" / "New creator").
 *  Only `update` carries information the pill can't ("Update" doesn't say
 *  which fields), so that's the one verb that keeps its detail line. */
function ConfirmRow({ summary }: { summary: ConfirmRowSummary }) {
  return (
    <div className="flex items-center gap-3 bg-card px-3 py-2 transition-colors hover:bg-muted/40">
      <RowNumberBadge n={summary.rowNumber} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <TruncatedText text={summary.username} className="min-w-0 text-body-sm-medium text-foreground" />
          <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-caption-medium', VERB_TONE[summary.verb])}>
            {VERB_LABEL[summary.verb]}
          </span>
        </div>
        {summary.verb === 'update' && (
          <TruncatedText
            as="p"
            text={summary.detail ?? ''}
            className="text-caption-regular text-muted-foreground"
          />
        )}
      </div>
    </div>
  );
}

export function ImportConfirmStep({ rows, expectedFields, busy, onCommit, onBack }: ImportConfirmStepProps) {
  const tally = computeImportTally(rows);
  const summaries = confirmRowSummaries(rows, expectedFields);
  const ledger = [
    tally.toAdd > 0 && `${tally.toAdd} added`,
    tally.toUpdate > 0 && `${tally.toUpdate} updated`,
    tally.toKeep > 0 && `${tally.toKeep} left as-is`,
    tally.toSkip > 0 && `${tally.toSkip} skipped`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex flex-col gap-4">
      {tally.total === 0 ? (
        <p className="text-body-sm-regular text-muted-foreground">Nothing to import — every row was dropped, skipped, or kept as-is.</p>
      ) : (
        <>
          <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 px-4 py-3">
            <ListChecks size={16} weight="bold" className="shrink-0 text-muted-foreground" />
            <span className="text-body-sm-medium text-foreground">{ledger}</span>
          </div>
          <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
            {summaries.map((s) => (
              <ConfirmRow key={s.rowNumber} summary={s} />
            ))}
          </div>
        </>
      )}

      <WizardFooter>
        <div className="flex w-full items-center justify-between">
          <Button type="button" variant="outline" size="lg" disabled={busy} onClick={() => { onBack().catch(() => {}); }}>
            Back
          </Button>
          <Button
            type="button"
            variant="default"
            size="lg"
            loading={busy}
            disabled={tally.total === 0}
            onClick={() => { onCommit().catch(() => {}); }}
          >
            {busy ? 'Starting import…' : 'Commit import'}
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}
